import React from 'react'
import { Button, Result, Spin, Typography } from 'antd'
import { authApi } from 'api/auth'
import { sellerApi } from 'api/seller'
import { ESellerStatus, ISellerStatusResponse } from 'models/seller'
import { appActions } from 'store/app'
import { SUPPORT_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'
import { useAppDispatch } from 'shared/hooks/useReduxHooks'
import { useLogoutConfirm } from 'shared/hooks/useLogoutConfirm'
import styles from './SellerStatus.module.scss'

type TResultStatus = 'info' | 'error' | 'warning'

const titles: Record<string, string> = {
  [ESellerStatus.WAITING_APPROVE]: 'Заявка на рассмотрении',
  [ESellerStatus.DECLINED]: 'Заявка отклонена',
  [ESellerStatus.ARCHIVED]: 'Магазин в архиве'
}

const subTitles: Record<string, string> = {
  [ESellerStatus.WAITING_APPROVE]:
    'Мы проверяем данные магазина, с вами свяжется менеджер. Кабинет откроется сразу после одобрения.',
  [ESellerStatus.DECLINED]: 'Кабинет закрыт.',
  [ESellerStatus.ARCHIVED]:
    'Магазин снят с публикации. Восстановить его может только техподдержка.'
}

const resultStatuses: Record<string, TResultStatus> = {
  [ESellerStatus.WAITING_APPROVE]: 'info',
  [ESellerStatus.DECLINED]: 'error',
  [ESellerStatus.ARCHIVED]: 'warning'
}

// Единственный экран, доступный продавцу без одобрения: остальной кабинет
// закрыт на бэкенде, поэтому вместо пустых ошибок показываем статус заявки
export const SellerStatus: React.FC = () => {
  const dispatch = useAppDispatch()
  const logout = useLogoutConfirm()
  const [seller, setSeller] = React.useState<ISellerStatusResponse | null>(null)
  const [isLoading, setIsLoading] = React.useState(true)

  const loadStatus = async () => {
    try {
      setIsLoading(true)
      const { data } = await sellerApi.getStatus()
      // Магазин одобрили, пока продавец сидел на этом экране: обновляем токен,
      // и роутер сам открывает кабинет
      if (data.status === ESellerStatus.APPROVED) {
        const response = await authApi.checkAuth()
        return dispatch(appActions.setToken(response.data))
      }
      setSeller(data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    loadStatus()
  }, [])

  const status = seller?.status ?? ESellerStatus.WAITING_APPROVE

  return (
    <div className={styles.content}>
      {isLoading && !seller ? (
        <Spin size='large' />
      ) : (
        <Result
          status={resultStatuses[status] ?? 'info'}
          title={titles[status] ?? 'Кабинет недоступен'}
          subTitle={
            <>
              {seller?.shop_name && (
                <Typography.Paragraph className={styles.shopName}>
                  {seller.shop_name}
                </Typography.Paragraph>
              )}
              <Typography.Paragraph>
                {subTitles[status] ?? 'Обратитесь в техподдержку.'}
              </Typography.Paragraph>
              {seller?.reason_of_cancel && (
                <Typography.Paragraph className={styles.reason}>
                  Причина: {seller.reason_of_cancel}
                </Typography.Paragraph>
              )}
              <Typography.Paragraph>
                <a href={SUPPORT_URL} target='_blank' rel='noreferrer'>
                  Написать в техподдержку
                </a>
              </Typography.Paragraph>
            </>
          }
          extra={[
            <Button key='refresh' type='primary' loading={isLoading} onClick={loadStatus}>
              Обновить статус
            </Button>,
            <Button key='logout' onClick={logout}>
              Выйти
            </Button>
          ]}
        />
      )}
    </div>
  )
}
